/**
 * GroupProxyForm — record a proxy read for a no-device group (VER-115, Model B).
 *
 * Two steps:
 *   1. Name the group — programme, session, published survey, and which administration this is.
 *   2. Administer that survey through the EXISTING builder engine (SurveyRunner → scoreSurvey),
 *      so reverse scoring, "every item required" validation, progress and the score panel are
 *      reused rather than reimplemented.
 *
 * The VER-57 shell this replaces hardcoded a 14-item WEMWBS grid with placeholder item text. That
 * is gone: item wording is licensed (safety non-negotiable #2), and scales are authored in the
 * survey builder now, so the form administers whichever published template the practitioner picks
 * instead of carrying a scale of its own.
 *
 * Submission goes to POST /survey-responses/proxy. The SERVER — not this form — decides the result
 * is ProxyEntered and stamps the recording user, so a client cannot mislabel provenance.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { Session } from "common/build/prisma/client";
import { SurveyRunner } from "../builder/SurveyRunner";
import { allQuestions, SurveyDefinition } from "../builder/survey.types";
import { questionScore } from "../builder/scoring";
import { toDefinition } from "../builder/builder.apiPersistence";
import {
    captureProxySurveyResponse,
    getSurveyTemplate,
    listSurveyTemplates,
    PersistedSurveyTemplate,
} from "../../../client/api/survey";
import { listAssessments, AssessmentCard } from "../../../client/api/assessment";
import * as sessionApi from "../../../client/api/session";
import Spinner from "../../layout/Spinner";

/**
 * Which administration this read represents. SurveyAdministration is the vocabulary outcome
 * reporting pairs on; the shell's "pre"/"post" wording had no counterpart in the schema.
 */
const ADMINISTRATIONS = [
    { value: "Baseline", label: "Baseline (first measurement)" },
    { value: "Interim", label: "Interim" },
    { value: "Endpoint", label: "Endpoint (final measurement)" },
] as const;

type Administration = (typeof ADMINISTRATIONS)[number]["value"];

export type GroupProxyFormProps = {
    /** Called once a proxy read has been persisted. */
    onSubmitSuccess: () => void;
    onCancel: () => void;
};

export const GroupProxyForm = ({ onSubmitSuccess, onCancel }: GroupProxyFormProps): JSX.Element => {
    const [programmes, setProgrammes] = useState<AssessmentCard[]>([]);
    const [templates, setTemplates] = useState<PersistedSurveyTemplate[]>([]);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const [programmeId, setProgrammeId] = useState<number | "">("");
    const [sessionId, setSessionId] = useState<number | "">("");
    const [templateId, setTemplateId] = useState<number | "">("");
    const [administration, setAdministration] = useState<Administration>("Baseline");

    const [definition, setDefinition] = useState<SurveyDefinition | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Only groups can have a group proxy read, and only PUBLISHED templates may be administered —
    // a draft's scoring polarity can still change, which would re-interpret stored answers.
    useEffect(() => {
        let cancelled = false;
        Promise.all([listAssessments(), listSurveyTemplates()])
            .then(([assessments, templateList]) => {
                if (cancelled) return;
                setProgrammes(assessments.items.filter((item) => item.mode === "group"));
                setTemplates(templateList.filter((template) => template.status === "Published"));
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setLoadError(error instanceof Error ? error.message : "Groups and surveys could not be loaded.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Sessions belong to a programme, so the list reloads whenever the group changes and any
    // previously chosen session is cleared — the server rejects a session from another programme.
    useEffect(() => {
        if (programmeId === "") {
            setSessions([]);
            return;
        }
        let cancelled = false;
        setSessionId("");
        sessionApi
            .list(programmeId)
            .then((list) => {
                if (!cancelled) setSessions(list);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setLoadError(error instanceof Error ? error.message : "Sessions could not be loaded.");
            });
        return () => {
            cancelled = true;
        };
    }, [programmeId]);

    const startAdministering = async () => {
        if (templateId === "") return;
        setSaveError(null);
        try {
            setDefinition(toDefinition(await getSurveyTemplate(templateId)));
        } catch (error: unknown) {
            setSaveError(error instanceof Error ? error.message : "That survey could not be loaded.");
        }
    };

    const ready = programmeId !== "" && sessionId !== "" && templateId !== "";

    if (loading) {
        return <Spinner />;
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    if (definition && programmeId !== "" && sessionId !== "" && templateId !== "") {
        return (
            <SurveyRunner
                survey={definition}
                previewBanner={
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You are recording this on the group&apos;s behalf. It is stored as a proxy read and reported
                        separately from participants&apos; own answers.
                    </Alert>
                }
                onExit={() => setDefinition(null)}
                submitting={saving}
                onScored={async (result, values) => {
                    if (result.finalScore === null || result.rawScore === null) {
                        return;
                    }
                    setSaving(true);
                    setSaveError(null);
                    try {
                        await captureProxySurveyResponse({
                            templateId,
                            programmeId,
                            sessionId,
                            // templateVersionId is omitted for the same reason as the 1-to-1 flow:
                            // the template API exposes an ordinal, not the version row id.
                            administration,
                            rawScore: result.rawScore,
                            finalScore: result.finalScore,
                            items: allQuestions(definition)
                                .filter((question) => question.scored && values[question.id] !== undefined)
                                .map((question) => ({
                                    questionKey: question.id,
                                    value: Number(values[question.id]),
                                    adjustedValue: questionScore(definition, question, values[question.id]),
                                })),
                        });
                        onSubmitSuccess();
                    } catch (error: unknown) {
                        // The endpoint rejects cross-tenant ids (404) and integrity breaches such as a
                        // session outside the programme (404/400). Surface the reason — a generic
                        // failure leaves the practitioner unable to tell retry from fix-the-selection.
                        setSaveError(
                            error instanceof Error ? error.message : "The read was scored but could not be saved.",
                        );
                    } finally {
                        setSaving(false);
                    }
                }}
                scoreFooter={
                    saveError ? (
                        <Alert severity="error">{saveError} The score above is correct — it has not been saved.</Alert>
                    ) : saving ? (
                        <Typography variant="body2">Saving…</Typography>
                    ) : null
                }
            />
        );
    }

    return (
        <Box>
            {saveError ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {saveError}
                </Alert>
            ) : null}

            {programmes.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    You have no group programmes to record against.
                </Alert>
            ) : null}

            {templates.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    No published surveys are available. Publish one in the survey builder first.
                </Alert>
            ) : null}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="proxy-programme-label">Group</InputLabel>
                        <Select
                            labelId="proxy-programme-label"
                            label="Group"
                            value={programmeId}
                            onChange={(event) => setProgrammeId(Number(event.target.value))}
                        >
                            {programmes.map((programme) => (
                                <MenuItem key={programme.id} value={programme.id}>
                                    {programme.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={programmeId === ""}>
                        <InputLabel id="proxy-session-label">Session</InputLabel>
                        <Select
                            labelId="proxy-session-label"
                            label="Session"
                            value={sessionId}
                            onChange={(event) => setSessionId(Number(event.target.value))}
                        >
                            {sessions.map((session) => (
                                <MenuItem key={session.id} value={session.id}>
                                    {`Session ${session.sessionNumber} — ${new Date(session.date).toLocaleDateString(
                                        "en-GB",
                                        { day: "numeric", month: "short", year: "numeric" },
                                    )}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="proxy-template-label">Survey</InputLabel>
                        <Select
                            labelId="proxy-template-label"
                            label="Survey"
                            value={templateId}
                            onChange={(event) => setTemplateId(Number(event.target.value))}
                        >
                            {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="proxy-administration-label">Administration</InputLabel>
                        <Select
                            labelId="proxy-administration-label"
                            label="Administration"
                            value={administration}
                            onChange={(event) => setAdministration(event.target.value as Administration)}
                        >
                            {ADMINISTRATIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Box mt={4} display="flex" gap={2}>
                <Button variant="contained" color="primary" disabled={!ready} onClick={startAdministering}>
                    Start the survey
                </Button>
                <Button variant="text" onClick={onCancel}>
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};
